import { z } from 'zod';
import { knowledgeCommandIdSchema, knowledgeReasonSchema, knowledgeSlugSchema, knowledgeVisibilityModeSchema, } from './knowledge-library.contract.js';
export const knowledgeCollectionsCapability = 'knowledge.collections.v1';
export const knowledgeAccessPolicyCapability = 'knowledge.access-policy.v1';
export const knowledgeReviewCapability = 'knowledge.review.v1';
export const knowledgePrincipalTypeSchema = z.enum(['role', 'permission', 'department', 'user']);
export const knowledgeAccessLevelSchema = z.enum(['view', 'edit', 'admin']);
export const knowledgeCollectionStatusSchema = z.enum(['active', 'archived']);
export const knowledgeReviewDecisionValueSchema = z.enum(['approved', 'changes_requested', 'skipped']);
export const knowledgeReviewStatusSchema = z.enum(['pending', 'changes_requested', 'approved', 'skipped']);
export const knowledgeAccessRuleInputSchema = z.strictObject({
    principalType: knowledgePrincipalTypeSchema,
    principalValue: z.string().trim().min(1).max(200),
    accessLevel: knowledgeAccessLevelSchema,
});
export const knowledgeAccessRuleSchema = knowledgeAccessRuleInputSchema.extend({
    id: z.string().uuid(), tenantId: z.string().uuid(),
});
export const knowledgeCollectionSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), parentCollectionId: z.string().uuid().nullable(),
    slug: knowledgeSlugSchema, title: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1_000).nullable(), iconKey: z.string().trim().max(80).nullable(),
    sortOrder: z.number().int().min(-100_000).max(100_000),
    visibilityMode: knowledgeVisibilityModeSchema, status: knowledgeCollectionStatusSchema,
    revision: z.number().int().positive(), createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }), rules: z.array(knowledgeAccessRuleSchema),
});
export const knowledgeCollectionsResponseSchema = z.strictObject({ data: z.array(knowledgeCollectionSchema) });
export const knowledgeCollectionResponseSchema = z.strictObject({
    data: knowledgeCollectionSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
const collectionFields = {
    parentCollectionId: z.string().uuid().nullable(), slug: knowledgeSlugSchema,
    title: z.string().trim().min(1).max(160), description: z.string().trim().max(1_000).nullable(),
    iconKey: z.string().trim().max(80).nullable(), sortOrder: z.number().int().min(-100_000).max(100_000),
    visibilityMode: knowledgeVisibilityModeSchema, rules: z.array(knowledgeAccessRuleInputSchema).max(250),
};
export const knowledgeCreateCollectionSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, ...collectionFields,
    reason: knowledgeReasonSchema,
});
export const knowledgeUpdateCollectionSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(),
    ...collectionFields, reason: knowledgeReasonSchema,
});
export const knowledgeArchiveCollectionSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(), reason: knowledgeReasonSchema,
});
export const knowledgeDocumentAccessPolicySchema = z.strictObject({
    documentId: z.string().uuid(), collectionId: z.string().uuid().nullable(),
    visibilityMode: knowledgeVisibilityModeSchema, revision: z.number().int().positive(),
    rules: z.array(knowledgeAccessRuleSchema),
});
export const knowledgeDocumentAccessPolicyResponseSchema = z.strictObject({
    data: knowledgeDocumentAccessPolicySchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const knowledgeConfigureDocumentAccessSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(),
    collectionId: z.string().uuid().nullable(), visibilityMode: knowledgeVisibilityModeSchema,
    rules: z.array(knowledgeAccessRuleInputSchema).max(250), reason: knowledgeReasonSchema,
});
export const knowledgeReviewDecisionSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), assignmentId: z.string().uuid(),
    sequence: z.number().int().positive(), decision: knowledgeReviewDecisionValueSchema,
    notes: z.string().trim().max(2_000).nullable(), decidedByMembershipId: z.string().uuid(),
    decidedAt: z.string().datetime({ offset: true }),
});
export const knowledgeReviewAssignmentSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), documentId: z.string().uuid(),
    versionId: z.string().uuid(), versionNumber: z.number().int().positive(), documentTitle: z.string().min(1).max(200),
    reviewerMembershipId: z.string().uuid(), assignedByMembershipId: z.string().uuid(),
    dueAt: z.string().datetime({ offset: true }).nullable(), assignmentNotes: z.string().trim().max(2_000).nullable(),
    status: knowledgeReviewStatusSchema, assignedAt: z.string().datetime({ offset: true }),
    decisions: z.array(knowledgeReviewDecisionSchema),
});
export const knowledgeReviewAssignmentsResponseSchema = z.strictObject({ data: z.array(knowledgeReviewAssignmentSchema) });
export const knowledgeReviewAssignmentResponseSchema = z.strictObject({
    data: knowledgeReviewAssignmentSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }),
});
export const knowledgeReviewListQuerySchema = z.strictObject({
    scope: z.enum(['mine', 'all']).default('mine'), status: knowledgeReviewStatusSchema.optional(),
    includeCompleted: z.boolean().default(true), limit: z.number().int().min(1).max(250).default(100),
    offset: z.number().int().nonnegative().default(0),
});
export const knowledgeAssignReviewSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, versionId: z.string().uuid(),
    reviewerMembershipId: z.string().uuid(), dueAt: z.string().datetime({ offset: true }).nullable(),
    notes: z.string().trim().max(2_000).nullable(), reason: knowledgeReasonSchema,
});
export const knowledgeDecideReviewSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, decision: knowledgeReviewDecisionValueSchema,
    notes: z.string().trim().max(2_000).nullable(), reason: knowledgeReasonSchema,
});
//# sourceMappingURL=knowledge-governance.contract.js.map